import { hoyLocal, fechaLocal } from '@/lib/fechas';
import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Printer, Calendar, DollarSign, CreditCard, Banknote, ArrowLeftRight, ChevronLeft, ChevronRight, FileText, TrendingUp, Wallet, TrendingDown, ArrowDownUp } from 'lucide-react';
import { useAuth } from '@/contexto/AuthContexto';

const formatoMXN = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const getWeekRange = (offset = 0) => {
  const now = new Date();
  now.setDate(now.getDate() + offset * 7);
  const day = now.getDay();
  const lunes = new Date(now);
  lunes.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  const domingo = new Date(lunes);
  domingo.setDate(lunes.getDate() + 6);
  return {
    inicio: fechaLocal(lunes),
    fin: fechaLocal(domingo),
  };
};

const formatFecha = (f) => {
  if (!f) return '';
  return new Date(f + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short' });
};

const METODO_LABELS = {
  efectivo: { label: 'Efectivo', icon: Banknote, color: 'text-emerald-400' },
  tarjeta: { label: 'Tarjeta', icon: CreditCard, color: 'text-sky-400' },
  transferencia: { label: 'Transferencia', icon: ArrowLeftRight, color: 'text-violet-400' },
  otro: { label: 'Otro', icon: Wallet, color: 'text-slate-400' },
};

const CAT_GASTOS_LABELS = {
  nomina: { label: 'Nomina', color: '#f97316' },
  refacciones: { label: 'Refacciones', color: '#06b6d4' },
  servicios: { label: 'Servicios', color: '#8b5cf6' },
  renta: { label: 'Renta', color: '#ec4899' },
  herramientas: { label: 'Herramientas', color: '#14b8a6' },
  otros: { label: 'Otros', color: '#64748b' },
};

export default function CorteCaja() {
  const { usuario } = useAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const printRef = useRef();

  const semana = getWeekRange(weekOffset);

  useEffect(() => {
    setCargando(true);
    api.get(`/corte-caja-semanal?inicio=${semana.inicio}&fin=${semana.fin}`)
      .then(r => setDatos(r.data))
      .catch(() => setDatos(null))
      .finally(() => setCargando(false));
  }, [weekOffset]);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(`
      <html>
        <head>
          <title>Corte de Caja - ${semana.inicio} a ${semana.fin}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; color: #1e293b; font-size: 13px; }
            h1 { font-size: 20px; margin-bottom: 4px; }
            h2 { font-size: 15px; margin: 16px 0 8px; color: #475569; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #f59e0b; padding-bottom: 12px; }
            .header small { color: #64748b; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 12px 0; }
            .stat { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px; text-align: center; }
            .stat .value { font-size: 20px; font-weight: bold; color: #0f172a; }
            .stat .label { font-size: 11px; color: #64748b; text-transform: uppercase; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
            th { background: #f1f5f9; text-align: left; padding: 6px 8px; border: 1px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #475569; }
            td { padding: 5px 8px; border: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .total-row td { font-weight: bold; background: #fef3c7; }
            .footer { margin-top: 24px; text-align: center; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 12px; }
            .metodo-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 8px; margin: 8px 0; }
            .metodo-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 8px; }
            .metodo-card .value { font-size: 16px; font-weight: bold; }
            .metodo-card .label { font-size: 11px; color: #64748b; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${usuario?.tenant_nombre || 'Talleres PRO'}</h1>
            <small>Corte de Caja Semanal: ${semana.inicio} al ${semana.fin}</small>
          </div>

          <div class="grid">
            <div class="stat">
              <div class="value">${formatoMXN(datos?.resumen?.total_cobrado)}</div>
              <div class="label">Total Cobrado</div>
            </div>
            <div class="stat">
              <div class="value" style="color:#ef4444">${formatoMXN(datos?.resumen?.total_gastos)}</div>
              <div class="label">Total Gastos</div>
            </div>
            <div class="stat">
              <div class="value" style="color:${datos?.resumen?.ganancia_neta >= 0 ? '#10b981' : '#ef4444'}">${formatoMXN(datos?.resumen?.ganancia_neta)}</div>
              <div class="label">Ganancia Neta</div>
            </div>
          </div>

          <h2>Gastos por Categoria</h2>
          <div class="metodo-grid">
            ${Object.entries(datos?.gastos_por_categoria || {}).map(([cat, d]) => `
              <div class="metodo-card">
                <div class="value" style="color:#ef4444">${formatoMXN(d.total)}</div>
                <div class="label">${CAT_GASTOS_LABELS[cat]?.label || cat} (${d.count})</div>
              </div>
            `).join('') || '<div class="metodo-card"><div class="label">Sin gastos registrados</div></div>'}
          </div>

          <h2>Por Metodo de Pago</h2>
          <div class="metodo-grid">
            ${Object.entries(datos?.por_metodo || {}).map(([m, d]) => `
              <div class="metodo-card">
                <div class="value">${formatoMXN(d.total)}</div>
                <div class="label">${METODO_LABELS[m]?.label || m} (${d.count} pagos)</div>
              </div>
            `).join('')}
          </div>

          <h2>Detalle de Pagos</h2>
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Orden</th>
                <th>Metodo</th>
                <th style="text-align:right">Monto</th>
                <th>Registrado por</th>
              </tr>
            </thead>
            <tbody>
              ${(datos?.pagos_detalle || []).map(p => `
                <tr>
                  <td>${p.fecha}</td>
                  <td>${p.cliente}</td>
                  <td>${p.num_orden}</td>
                  <td>${METODO_LABELS[p.metodo_pago]?.label || p.metodo_pago}</td>
                  <td style="text-align:right">${formatoMXN(p.monto)}</td>
                  <td>${p.creado_por}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="4">TOTAL</td>
                <td style="text-align:right">${formatoMXN(datos?.resumen?.total_cobrado)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            Generado el ${new Date().toLocaleString('es-MX')} - Talleres PRO
          </div>
        </body>
      </html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <div className="space-y-6" data-testid="corte-caja-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow text-2xl font-bold text-slate-50 uppercase tracking-wide">Corte de Caja Semanal</h1>
          <p className="text-sm text-slate-500 mt-1">Resumen financiero de la semana</p>
        </div>
        <Button onClick={handlePrint} disabled={!datos}
          className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm"
          data-testid="imprimir-corte-btn">
          <Printer className="w-4 h-4 mr-2" /> Imprimir Reporte
        </Button>
      </div>

      {/* Week navigator */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(weekOffset - 1)}
          className="border-slate-700 text-slate-400 hover:bg-slate-800 rounded-sm"
          data-testid="semana-anterior-btn">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="text-center min-w-[240px]">
          <div className="flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4 text-amber-500" />
            <span className="font-barlow font-bold text-slate-100">
              {formatFecha(semana.inicio)} - {formatFecha(semana.fin)}
            </span>
          </div>
          {weekOffset === 0 && <p className="text-xs text-amber-500 mt-0.5">Semana actual</p>}
        </div>
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(weekOffset + 1)}
          disabled={weekOffset >= 0}
          className="border-slate-700 text-slate-400 hover:bg-slate-800 rounded-sm"
          data-testid="semana-siguiente-btn">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {cargando ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Card key={i} className="bg-slate-900/60 border-slate-800 h-24 animate-pulse" />)}
        </div>
      ) : !datos ? (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-8 text-center text-slate-500">No se encontraron datos para esta semana</CardContent>
        </Card>
      ) : (
        <div ref={printRef}>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-emerald-400 font-barlow">{formatoMXN(datos.resumen.total_cobrado)}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Total Cobrado</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-red-500/10 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-red-400 font-barlow">{formatoMXN(datos.resumen.total_gastos)}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Total Gastos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={`border-slate-800 ${datos.resumen.ganancia_neta >= 0 ? 'bg-emerald-950/30 border-emerald-800/50' : 'bg-red-950/30 border-red-800/50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${datos.resumen.ganancia_neta >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                    <ArrowDownUp className={`w-5 h-5 ${datos.resumen.ganancia_neta >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <p className={`text-xl font-bold font-barlow ${datos.resumen.ganancia_neta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatoMXN(datos.resumen.ganancia_neta)}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Ganancia Neta</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-amber-500 font-barlow">{datos.resumen.total_ordenes}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Ordenes ({datos.resumen.ordenes_completadas} cerradas)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-sky-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-sky-400 font-barlow">{formatoMXN(datos.resumen.total_facturado)}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Total Facturado</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-orange-500/10 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-orange-400 font-barlow">{formatoMXN(datos.resumen.total_pendiente)}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Saldo Pendiente</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* By payment method */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {Object.entries(datos.por_metodo).map(([metodo, d]) => {
              const config = METODO_LABELS[metodo] || METODO_LABELS.otro;
              const Icon = config.icon;
              return (
                <Card key={metodo} className="bg-slate-900/60 border-slate-800">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${config.color}`} />
                      <span className="text-xs text-slate-400 uppercase">{config.label}</span>
                    </div>
                    <p className={`text-lg font-bold font-barlow ${config.color}`}>{formatoMXN(d.total)}</p>
                    <p className="text-[10px] text-slate-600">{d.count} pago{d.count !== 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Gastos por categoria */}
          {Object.keys(datos.gastos_por_categoria).length > 0 && (
            <Card className="bg-slate-900/60 border-slate-800 mb-6">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-red-400 font-bold mb-3 flex items-center gap-1">
                  <TrendingDown className="w-3.5 h-3.5" /> Gastos por Categoria
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(datos.gastos_por_categoria).map(([cat, d]) => {
                    const cfg = CAT_GASTOS_LABELS[cat] || CAT_GASTOS_LABELS.otros;
                    const pct = datos.resumen.total_gastos > 0 ? Math.round((d.total / datos.resumen.total_gastos) * 100) : 0;
                    return (
                      <div key={cat} className="bg-slate-800/50 rounded-sm p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-400 uppercase">{cfg.label}</span>
                          <span className="text-[10px] text-slate-600">{pct}%</span>
                        </div>
                        <p className="text-lg font-bold text-red-400 font-barlow">{formatoMXN(d.total)}</p>
                        <div className="w-full bg-slate-700/50 rounded-full h-1.5 mt-2">
                          <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: cfg.color }} />
                        </div>
                        <p className="text-[10px] text-slate-600 mt-1">{d.count} gasto{d.count !== 1 ? 's' : ''}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily breakdown */}
          {Object.keys(datos.por_dia).length > 0 && (
            <Card className="bg-slate-900/60 border-slate-800 mb-6">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-3">Cobros por Dia</h3>
                <div className="flex items-end gap-2 h-32">
                  {(() => {
                    const dias = [];
                    const d = new Date(semana.inicio + 'T12:00:00');
                    for (let i = 0; i < 7; i++) {
                      dias.push(fechaLocal(d));
                      d.setDate(d.getDate() + 1);
                    }
                    const max = Math.max(...dias.map(dia => datos.por_dia[dia] || 0), 1);
                    return dias.map(dia => {
                      const val = datos.por_dia[dia] || 0;
                      const pct = (val / max) * 100;
                      return (
                        <div key={dia} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[10px] text-slate-500 font-mono">{val > 0 ? formatoMXN(val) : ''}</span>
                          <div className="w-full bg-slate-800 rounded-t-sm relative" style={{ height: '80px' }}>
                            <div className="absolute bottom-0 w-full bg-amber-500/60 rounded-t-sm transition-all"
                              style={{ height: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-600">{formatFecha(dia)?.split(' ')[0]}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment detail table */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-3">Detalle de Pagos ({datos.pagos_detalle.length})</h3>
              {datos.pagos_detalle.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800/50">
                        <TableHead className="text-slate-500 text-xs">Fecha</TableHead>
                        <TableHead className="text-slate-500 text-xs">Cliente</TableHead>
                        <TableHead className="text-slate-500 text-xs">Orden</TableHead>
                        <TableHead className="text-slate-500 text-xs">Metodo</TableHead>
                        <TableHead className="text-slate-500 text-xs text-right">Monto</TableHead>
                        <TableHead className="text-slate-500 text-xs">Registrado por</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {datos.pagos_detalle.map((p, i) => {
                        const config = METODO_LABELS[p.metodo_pago] || METODO_LABELS.otro;
                        return (
                          <TableRow key={i} className="border-slate-800/30">
                            <TableCell className="text-sm text-slate-300">{formatFecha(p.fecha)}</TableCell>
                            <TableCell className="text-sm text-slate-200 font-medium">{p.cliente || '-'}</TableCell>
                            <TableCell className="text-sm text-slate-400 font-mono">{p.num_orden || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${config.color} border-slate-700`}>
                                {config.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-right text-emerald-400 font-mono font-semibold">{formatoMXN(p.monto)}</TableCell>
                            <TableCell className="text-xs text-slate-500">{p.creado_por || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-slate-600 text-center py-4">No hay pagos registrados en esta semana</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
